/**
 * theme — device-local appearance choice (System / Light / Dark) and its
 * resolution to an effective light|dark palette (T12.2 step 10a).
 *
 * Split from the React provider so the pure bits (validation, persistence,
 * matchMedia resolution) stay side-effect-light and unit-testable. The provider
 * (`ThemeProvider.tsx`) owns the React state + the `<html data-theme>` effect.
 *
 * Persistence key: `app-theme` (device-local, like `app-mode`). The choice is a
 * device preference — it is NOT synced to the household (a phone in a dark room
 * and a laptop at a desk can differ), consistent with how `app-mode` is stored.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'app-theme';

const CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/** Type guard — a stored / inbound value is a valid theme choice. */
export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (CHOICES as readonly string[]).includes(value);
}

/**
 * Read the persisted theme choice from `localStorage`. Safe at module-load
 * time — returns `'system'` (the default) if nothing valid is persisted or if
 * storage is inaccessible (incognito, SSR).
 */
export function readPersistedThemeChoice(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** Persist the theme choice. Best-effort — swallows storage failures. */
export function persistThemeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // In-memory only for this session if storage is unavailable.
  }
}

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/** The OS-level dark-mode preference, or `false` if matchMedia is unavailable. */
export function prefersDark(): boolean {
  try {
    return window.matchMedia(DARK_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * Resolve a choice to the effective palette. `system` consults the OS via
 * `prefersDark()`; `light` / `dark` are explicit.
 */
export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ResolvedTheme {
  if (choice === 'system') return systemPrefersDark ? 'dark' : 'light';
  return choice;
}

/**
 * Apply the resolved theme to the document root by setting / clearing
 * `data-theme="dark"`, and keep the `theme-color` meta (Android status-bar
 * scrim) in sync so the exposed body regions follow the in-app theme rather
 * than the OS `prefers-color-scheme`. Light is the default `:root` palette, so
 * light clears the attribute rather than setting `data-theme="light"`. No-op if
 * `document` is unavailable.
 *
 * The meta content is read from the live document background (set by the
 * index.html inline `<style>` per `data-theme`) rather than hardcoded hexes, so
 * it stays exactly consistent with the rendered background and carries no token
 * values in this file (CP5). `data-theme` is applied FIRST so the computed
 * background reflects the new theme before it is read.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  try {
    const root = document.documentElement;
    if (resolved === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && document.body) {
      const bg = getComputedStyle(document.body).backgroundColor;
      if (bg) meta.setAttribute('content', bg);
    }
  } catch {
    // No DOM (SSR / non-browser) — nothing to apply.
  }
}

/**
 * Subscribe to OS dark-mode changes. Returns an unsubscribe function. Used by
 * the provider so a `system` choice live-updates when the OS theme flips while
 * the app is open. No-op (returns a noop unsubscribe) if matchMedia is absent.
 */
export function subscribeSystemTheme(onChange: (systemPrefersDark: boolean) => void): () => void {
  try {
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent): void => onChange(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  } catch {
    return () => undefined;
  }
}
